import React, { useState } from "react";
import axios from "axios";

const API = "/api";
const MARKETPLACES = [
  { id: "allegro-pl", label: "PL" },
  { id: "allegro-business-pl", label: "Business PL" },
  { id: "allegro-cz", label: "CZ" },
  { id: "allegro-sk", label: "SK" },
  { id: "allegro-hu", label: "HU" },
  { id: "allegro-business-cz", label: "Business CZ" },
];

interface Props {
  offerIds: string[];
  preselectedTargetId?: string;
  preselectedOtherName?: string;
  accounts: any[];
  currentUser: any;
  onClose: () => void;
  onDone: () => void;
}

interface PreparedOffer {
  id: string;
  name: string;
  image_url: string;
  price: number;
  category_id: string;
  title: string;      // edytowalny tytul
  newPrice: string;   // edytowalna cena
  marketplaces?: string[];      // rynki oryginalu
  vat_rates?: any;              // VAT z oryginalu
  selectedMarkets: string[];    // zaznaczone rynki
}

export default function CopyToAccountWizard({ offerIds, accounts, currentUser, onClose, onDone, preselectedTargetId, preselectedOtherName }: Props) {
  const [step, setStep] = useState<1|2>(1);
  const [mode, setMode] = useState<"own"|"other">("own");
  const [ownAccountId, setOwnAccountId] = useState("");
  const [otherName, setOtherName] = useState("");
  const [checking, setChecking] = useState(false);
  const [checkResult, setCheckResult] = useState<any>(null);
  const [confirmCross, setConfirmCross] = useState(false);
  const [prepared, setPrepared] = useState<PreparedOffer[]>([]);
  const [profile, setProfile] = useState<any>(null);
  const [missing, setMissing] = useState<string[]>([]);
  const [fields, setFields] = useState<any>({});
  const [publishing, setPublishing] = useState(false);
  const [generatingTitles, setGeneratingTitles] = useState(false);
  const [result, setResult] = useState<any>(null);
  React.useEffect(() => {
    // preselekcja konta docelowego z porownania kont
    if (preselectedOtherName) { setMode("other"); setOtherName(preselectedOtherName); }
    else if (preselectedTargetId) { setMode("own"); setOwnAccountId(preselectedTargetId); }
    // eslint-disable-next-line
  }, []);
  const checkAccount = async () => {
    setChecking(true); setCheckResult(null); setResult(null);
    try {
      const body: any = { source_offer_ids: offerIds };
      if (mode === "own") { body.target_account_id = ownAccountId; }
      else { body.target_account_name = otherName.trim(); }
      const r = await axios.post(API + "/offers/copy/prepare", body);
      if (!r.data.found) { setCheckResult({ found: false, detail: r.data.detail || "Nie znaleziono konta" }); setChecking(false); return; }
      setCheckResult(r.data);
      setProfile(r.data.profile);
      setMissing(r.data.missing || []);
      setFields({
        shipping_rates_id: r.data.profile.shipping_rates?.[0]?.id || "",
        return_policy_id: r.data.profile.return_policies?.[0]?.id || "",
        implied_warranty_id: r.data.profile.implied_warranties?.[0]?.id || "",
        warranty_id: r.data.profile.warranties?.[0]?.id || "",
        responsible_person_id: r.data.profile.responsible_persons?.[0]?.id || "",
        responsible_producer_id: r.data.profile.responsible_producers?.[0]?.id || "",
      });
      setPrepared((r.data.offers || []).map((o: any) => ({ ...o, title: o.name, newPrice: String(o.price), selectedMarkets: o.marketplaces || ["allegro-pl"] })));
    } catch (e: any) {
      setCheckResult({ found: false, detail: e.response?.data?.detail || "Blad walidacji konta" });
    }
    setChecking(false);
  };
  const genTitle = async (idx: number) => {
    const o = prepared[idx];
    setGeneratingTitles(true);
    try {
      const r = await axios.post(API + "/offers/generate-titles", {
        account_id: checkResult.target_account_id, category_name: "", context: o.name,
        account_names: [checkResult.target_account_name], other_titles: [],
      });
      const t = r.data.titles?.[checkResult.target_account_name] || (r.data.titles && Object.values(r.data.titles)[0]) || "";
      if (t) setPrepared(prev => prev.map((x, i) => i === idx ? { ...x, title: t as string } : x));
    } catch {}
    setGeneratingTitles(false);
  };
  const genAllTitles = async () => {
    setGeneratingTitles(true);
    for (let i = 0; i < prepared.length; i++) { await genTitle(i); }
    setGeneratingTitles(false);
  };
  const doPublish = async () => {
    setPublishing(true); setResult(null);
    try {
      const r = await axios.post(API + "/offers/copy/publish", {
        target_account_id: checkResult.target_account_id,
        ...fields,
        invoice: "VAT",
        location_city: profile?.default_location_city || null,
        location_zip: profile?.default_location_zip || null,
        location_province: profile?.default_location_province || null,
        confirm_cross_tenant: confirmCross,
        items: prepared.map(o => ({ offer_id: o.id, title: o.title, price: parseFloat(o.newPrice) || o.price, marketplaces: o.selectedMarkets, vat_rates: o.vat_rates || null })),
      });
      setResult(r.data);
      if (r.data.published > 0) onDone();
    } catch (e: any) {
      setResult({ error: e.response?.data?.detail || "Blad wystawiania" });
    }
    setPublishing(false);
  };
  const st: any = {
    overlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 20 },
    box: { background: "#fff", borderRadius: 12, padding: 24, width: 640, maxWidth: "95vw", maxHeight: "90vh", overflowY: "auto" },
    label: { fontSize: 12, color: "#6b7280", display: "block", marginBottom: 6 },
    input: { width: "100%", boxSizing: "border-box", padding: "8px 10px", border: "1px solid #d1d5db", borderRadius: 6, fontSize: 14 },
    btn: { padding: "8px 16px", borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: "pointer", border: "none" },
  };
  return (
    <div style={st.overlay} onClick={onClose}>
      <div style={st.box} onClick={e => e.stopPropagation()}>
        <div style={{ fontSize: 17, fontWeight: 700, marginBottom: 16 }}>📋 Kopiuj {offerIds.length} ofert na konto</div>

        <div style={{ marginBottom: 14 }}>
          <label style={st.label}>Konto docelowe (moja firma)</label>
          <select value={mode === "own" ? ownAccountId : ""} onChange={e => { setMode("own"); setOwnAccountId(e.target.value); setOtherName(""); setCheckResult(null); }} style={st.input}>
            <option value="">— wybierz moje konto —</option>
            {accounts.map((a: any) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
        </div>

        {currentUser?.can_cross_publish && (
          <div style={{ marginBottom: 14 }}>
            <label style={st.label}>...albo wpisz nazwę konta innej firmy</label>
            <input type="text" value={otherName} onChange={e => { setOtherName(e.target.value); if (e.target.value.trim()) { setMode("other"); setOwnAccountId(""); } setCheckResult(null); }} placeholder="np. motoLEDy" style={st.input} />
          </div>
        )}

        <button onClick={checkAccount} disabled={checking || (mode === "own" ? !ownAccountId : !otherName.trim())} style={{ ...st.btn, background: "#1d4ed8", color: "#fff", marginBottom: 14 }}>
          {checking ? "Sprawdzam..." : "Sprawdź konto"}
        </button>

        {checkResult && !checkResult.found && (
          <div style={{ padding: 10, background: "#fef2f2", color: "#dc2626", borderRadius: 8, fontSize: 13, marginBottom: 14 }}>✗ {checkResult.detail}</div>
        )}
        {checkResult && checkResult.found && (
          <div style={{ padding: 10, background: "#f0fdf4", color: "#15803d", borderRadius: 8, fontSize: 13, marginBottom: 14 }}>
            ✓ Znaleziono: <b>{checkResult.target_account_name}</b> (firma: {checkResult.company_name})
          </div>
        )}

        {checkResult && checkResult.found && checkResult.is_cross_tenant && (
          <div style={{ marginBottom: 14, padding: 12, background: "#fef2f2", border: "2px solid #dc2626", borderRadius: 8 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#dc2626", marginBottom: 8 }}>⚠️ UWAGA: Wystawiasz oferty na koncie, które NIE należy do Twojej firmy!</div>
            <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 13, color: "#374151" }}>
              <input type="checkbox" checked={confirmCross} onChange={e => setConfirmCross(e.target.checked)} style={{ width: 16, height: 16 }} />
              Potwierdzam wystawienie na cudzym koncie
            </label>
          </div>
        )}

        {checkResult && checkResult.found && missing.length > 0 && (
          <div style={{ marginBottom: 14, padding: 12, background: "#fef9c3", border: "1px solid #eab308", borderRadius: 8, fontSize: 13, color: "#92400e" }}>
            ⚠️ Profil konta docelowego jest niekompletny. Uzupełnij: {missing.join(", ")}. Wystawienie zablokowane.
          </div>
        )}

        {checkResult && checkResult.found && missing.length === 0 && (
          <>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 14 }}>
              <div>
                <label style={st.label}>Cennik wysyłek</label>
                <select value={fields.shipping_rates_id} onChange={e => setFields({ ...fields, shipping_rates_id: e.target.value })} style={st.input}>
                  {profile.shipping_rates.map((x: any) => <option key={x.id} value={x.id}>{x.name}</option>)}
                </select>
              </div>
              <div>
                <label style={st.label}>Polityka zwrotów</label>
                <select value={fields.return_policy_id} onChange={e => setFields({ ...fields, return_policy_id: e.target.value })} style={st.input}>
                  {profile.return_policies.map((x: any) => <option key={x.id} value={x.id}>{x.name}</option>)}
                </select>
              </div>
              <div>
                <label style={st.label}>Reklamacje (rękojmia)</label>
                <select value={fields.implied_warranty_id} onChange={e => setFields({ ...fields, implied_warranty_id: e.target.value })} style={st.input}>
                  {profile.implied_warranties.map((x: any) => <option key={x.id} value={x.id}>{x.name}</option>)}
                </select>
              </div>
              <div>
                <label style={st.label}>Gwarancja</label>
                <select value={fields.warranty_id} onChange={e => setFields({ ...fields, warranty_id: e.target.value })} style={st.input}>
                  <option value="">— brak —</option>
                  {profile.warranties.map((x: any) => <option key={x.id} value={x.id}>{x.name}</option>)}
                </select>
              </div>
              <div>
                <label style={st.label}>Osoba odpowiedzialna</label>
                <select value={fields.responsible_person_id} onChange={e => setFields({ ...fields, responsible_person_id: e.target.value })} style={st.input}>
                  {profile.responsible_persons.map((x: any) => <option key={x.id} value={x.id}>{x.name}</option>)}
                </select>
              </div>
              <div>
                <label style={st.label}>Producent odpowiedzialny</label>
                <select value={fields.responsible_producer_id} onChange={e => setFields({ ...fields, responsible_producer_id: e.target.value })} style={st.input}>
                  {profile.responsible_producers.map((x: any) => <option key={x.id} value={x.id}>{x.name}</option>)}
                </select>
              </div>
            </div>

            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#374151" }}>Oferty do skopiowania ({prepared.length})</div>
              <button onClick={genAllTitles} disabled={generatingTitles} style={{ ...st.btn, background: "#7c3aed", color: "#fff", fontSize: 12 }}>{generatingTitles ? "Generuję..." : "✦ Generuj tytuły AI dla wszystkich"}</button>
            </div>

            <div style={{ marginBottom: 14 }}>
              {prepared.map((o, idx) => (
                <div key={o.id} style={{ display: "flex", gap: 10, padding: 10, border: "1px solid #e5e7eb", borderRadius: 8, marginBottom: 8, alignItems: "flex-start" }}>
                  {o.image_url ? <img src={o.image_url} alt="" style={{ width: 56, height: 56, objectFit: "cover", borderRadius: 6, flexShrink: 0 }} /> : <div style={{ width: 56, height: 56, background: "#f3f4f6", borderRadius: 6, flexShrink: 0 }} />}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                      <span style={{ fontSize: 11, color: (o.title || "").length > 75 ? "#dc2626" : "#9ca3af" }}>{(o.title || "").length}/75</span>
                      <button onClick={() => genTitle(idx)} disabled={generatingTitles} style={{ ...st.btn, background: "#f3f4f6", color: "#7c3aed", fontSize: 11, padding: "3px 8px" }}>✦ AI</button>
                    </div>
                    <input value={o.title} onChange={e => setPrepared(prev => prev.map((x, i) => i === idx ? { ...x, title: e.target.value } : x))} maxLength={80} style={{ ...st.input, marginBottom: 6, borderColor: (o.title || "").length > 75 ? "#dc2626" : "#d1d5db" }} />
                    {(o as any).sku && <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 6 }}>SKU: {(o as any).sku} · nr: {o.id}</div>}
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <label style={{ fontSize: 12, color: "#6b7280" }}>Cena:</label>
                      <input type="number" step="0.01" value={o.newPrice} onChange={e => setPrepared(prev => prev.map((x, i) => i === idx ? { ...x, newPrice: e.target.value } : x))} style={{ ...st.input, width: 120 }} />
                      <span style={{ fontSize: 12, color: "#9ca3af" }}>PLN (oryg. {o.price})</span>
                    </div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 6 }}>
                      <span style={{ fontSize: 11, color: "#6b7280" }}>Rynki:</span>
                      {MARKETPLACES.filter(m => (o.marketplaces || ["allegro-pl"]).includes(m.id)).map(m => (
                        <label key={m.id} style={{ display: "flex", alignItems: "center", gap: 3, fontSize: 11, cursor: "pointer" }}>
                          <input type="checkbox" checked={o.selectedMarkets.includes(m.id)} disabled={m.id === "allegro-pl"} onChange={e => setPrepared(prev => prev.map((x, i) => i === idx ? { ...x, selectedMarkets: e.target.checked ? [...x.selectedMarkets, m.id] : x.selectedMarkets.filter(mm => mm !== m.id) } : x))} />
                          {m.label}
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {result && (
              <div style={{ marginBottom: 14 }}>
                <div style={{ padding: 10, background: (result.error || result.published === 0) ? "#fef2f2" : "#f0fdf4", borderRadius: 8, fontSize: 13, color: (result.error || result.published === 0) ? "#dc2626" : "#15803d" }}>
                  {result.error ? result.error : "Wystawiono: " + result.published + " z " + result.total}
                </div>
                {result.results && result.results.filter((r: any) => !r.success).map((r: any, i: number) => {
                  const errs = r.errors || [];
                  const first = errs[0] || {};
                  const meta = first.metadata || {};
                  let msg = first.userMessage || first.message || r.error || "Nieznany błąd";
                  if (meta.parameterName && meta.expectedParameterValue) {
                    msg = "Parametr „" + meta.parameterName + "\u201d nie zgadza się z katalogiem Allegro. Ustaw wartość: „" + meta.expectedParameterValue + "\u201d (obecnie: „" + (meta.currentParameterValue || "?") + "\u201d).";
                  }
                  return (
                    <div key={i} style={{ marginTop: 6, padding: 10, background: "#fff7ed", border: "1px solid #fdba74", borderRadius: 8, fontSize: 12, color: "#9a3412" }}>
                      <b>Oferta nr {r.offer_id}{(() => { const p = prepared.find((x: any) => x.id === r.offer_id); return p && (p as any).sku ? " (SKU: " + (p as any).sku + ")" : ""; })()}:</b> {msg}
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 8 }}>
          <button onClick={onClose} style={{ ...st.btn, background: "#f3f4f6", color: "#374151", border: "1px solid #d1d5db" }}>Zamknij</button>
          {checkResult && checkResult.found && missing.length === 0 && (
            <button onClick={doPublish} disabled={publishing || (checkResult.is_cross_tenant && !confirmCross)} style={{ ...st.btn, background: (publishing || (checkResult.is_cross_tenant && !confirmCross)) ? "#9ca3af" : "#0891b2", color: "#fff" }}>{publishing ? "Wystawiam..." : "Wystaw " + prepared.length + " ofert"}</button>
          )}
        </div>
      </div>
    </div>
  );
}

